/**
 * The multi-photo message bubble (R2): 1 photo full width, 2 side by side,
 * 3 as one wide tile over two, 4+ as a 2x2 grid with a "+N" overlay on the
 * last visible tile past four. Order is preserved exactly as
 * `mediaObjectIds` arrived from the server (R2's "order preserved").
 *
 * Every tile resolves `mediaId` -> URL through media-api's own
 * `/object/get-batch`, never a client-derived path, per
 * `server-resolves-client-renders.mdc`. Three states per tile, and they must
 * stay visually distinct (ISSUE-370): a skeleton while the batch is in
 * flight, the real photo once it resolves to `published`, and a neutral
 * "Photo unavailable" placeholder for anything else -- a rejected object, a
 * status other than `published`, a media id missing from the response
 * entirely (gone), or the batch call itself failing. ISSUE-370 is exactly
 * the bug this last case exists to not repeat: a broken image icon with no
 * fallback.
 *
 * `chatDetails/useChatMediaUrls.ts` already wraps this same call for the
 * `Media` tab, but it collapses "still loading" and "resolved to nothing"
 * into one `null`, which is exactly the distinction this bubble needs to
 * keep apart. So this component calls `useObjectGetBatch` directly rather
 * than reusing that hook, to keep `query.isPending` in hand.
 */
import { useObjectGetBatch } from "@sportsmet/media-sdk/private";
import { Skeleton } from "@sportsmet/ui";
import type { ReactNode } from "react";

import { PHOTO_UNAVAILABLE_LABEL } from "./chatExtrasCopy";
import type { typeMentionRun } from "./messageText";
import { MessageRunsText } from "./MessageRunsText";

export type typeMultiPhotoBubbleProps = {
  mediaObjectIds: string[];
  captionRuns: typeMentionRun[];
};

/** ISSUE-370 R4: a plain photo outline, never a struck-through or warning
 *  glyph. A photo that is gone is not an error, so the tile must not carry
 *  the broken-image mark that a slash across this frame would give it. */
function PhotoPlaceholderIcon(): ReactNode {
  return (
    <svg
      className="h-icon-md w-icon-md"
      viewBox="0 0 20 20"
      fill="none"
      aria-hidden="true"
      focusable="false"
    >
      <rect
        x="2.5"
        y="4"
        width="15"
        height="12"
        rx="1.5"
        stroke="currentColor"
        strokeWidth="1.5"
      />
      <circle cx="7" cy="9" r="1.25" fill="currentColor" />
      <path
        d="m4 14 4-4 3 3 2-2 3 3"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

/** 2x2 for 4+, one wide tile over two for 3, side by side for 2, one tile
 *  for 1. `total` is the visible tile count (capped at 4; anything past
 *  that rides along under the last tile's "+N" overlay). */
function gridClassNameFor(count: number): string {
  if (count === 1) return "grid grid-cols-1";
  if (count === 3) return "grid grid-cols-2 grid-rows-2";
  return "grid grid-cols-2";
}

function tileClassNameFor(index: number, count: number): string {
  if (count === 3 && index === 0) return "col-span-2 aspect-video";
  return "aspect-square";
}

export const MultiPhotoBubble = (
  props: typeMultiPhotoBubbleProps,
): ReactNode => {
  const { mediaObjectIds, captionRuns } = props;
  const query = useObjectGetBatch(
    { mediaIds: mediaObjectIds },
    { enabled: mediaObjectIds.length > 0 },
  );

  const visibleIds = mediaObjectIds.slice(0, 4);
  const overflowCount = mediaObjectIds.length - visibleIds.length;
  const itemsById = new Map(
    (query.data?.data?.items ?? []).map((item) => [item.mediaId, item]),
  );
  const isLoading = query.isPending;
  const isBatchFailed = query.isError || query.data?.error != null;

  return (
    <div className="flex flex-col gap-1 overflow-hidden rounded-md">
      <div className={gridClassNameFor(visibleIds.length)}>
        {visibleIds.map((mediaId, index) => {
          const item = itemsById.get(mediaId);
          const isLastVisible = index === visibleIds.length - 1;
          const showOverflowBadge = isLastVisible && overflowCount > 0;

          let tileContent: ReactNode;
          if (isLoading) {
            tileContent = (
              <div className="h-full w-full" data-testid="photo-tile-loading">
                <Skeleton shape="card" className="h-full w-full rounded-none" />
              </div>
            );
          } else if (
            isBatchFailed ||
            item?.status !== "published" ||
            !item.variants
          ) {
            tileContent = (
              <div
                className="flex h-full w-full flex-col items-center justify-center gap-1 bg-surface-sunken text-ink-tertiary"
                data-testid="photo-tile-unavailable"
              >
                <PhotoPlaceholderIcon />
                <span className="text-caption">{PHOTO_UNAVAILABLE_LABEL}</span>
              </div>
            );
          } else {
            tileContent = (
              <img
                src={item.variants.large.url}
                alt=""
                className="h-full w-full object-cover"
                data-testid="photo-tile-image"
              />
            );
          }

          return (
            <div
              key={mediaId}
              className={`relative overflow-hidden bg-surface-sunken ${tileClassNameFor(index, visibleIds.length)}`}
              data-testid="photo-tile"
            >
              {tileContent}
              {showOverflowBadge ? (
                <div
                  className="absolute inset-0 flex items-center justify-center bg-scrim text-h2 text-on-primary"
                  data-testid="photo-tile-overflow-badge"
                >
                  +{overflowCount}
                </div>
              ) : null}
            </div>
          );
        })}
      </div>
      {captionRuns.length > 0 ? (
        <p className="px-1 text-body text-ink">
          <MessageRunsText runs={captionRuns} />
        </p>
      ) : null}
    </div>
  );
};
